import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import Modal from "react-modal";
import { toggleLoading, setAlert } from "../../state/creators";
import { Form } from "../../styles/forms";
import Row from "../../styles/layout/row";
import Col from "../../styles/layout/col";
import { ModalWrapper, ModalContent, ModalClose, ModalHeader, ModalFooter, ModalStylesLarge, ModalToggleButtons, ModalCycleButton } from "../../styles/modals";
import { H2 } from "../../styles/type/headings";
import { ButtonList, ExportCtaAnchor, StandardButtonAnchor, StandardCancelAnchor } from "../../styles/type/buttons";
import { DocumentInterface } from "../../_types/document";
import SelectInput from "../forms/inputs/select";
import DateInput from "../forms/inputs/date";
import FormError from "../forms/errors/http";
import moment from "moment";
import { Note } from "../../styles/forms/inputs";
import InputHelp from "../forms/inputs/help";
import HelpModal from "./help.modal";
import { DocumentApprovalStatusHelpText, DocumentCitationHelpText } from "../../config/helpText";
import { MethodOfCitationOptions } from "../../config/formData";
import CheckboxInput from "../forms/inputs/checkbox";
import { useCustomerDocuments } from "../../hooks/useCustomerDocuments";
import { RightToWork, rightToWorkCitationDocumentIds } from "../../_enums/rightToWork.enum";
import { MethodOfCitation } from "../../_enums/methodOfCitation.enum";
import FilePreview from "../documents/preview";
import { Divider } from "../../styles/content";
import FilePreviewModal from "./filePreviewModal";
import TextareaInput from "../forms/inputs/textarea";
import { ValidateDocumentCitationCompliance } from "../../functions/validators";

type Props = {
	closeModal: () => void;
	confirmButtonAction: (document: DocumentInterface) => void;
	downloadDocument?: (document: DocumentInterface) => void;
	deleteDocument?: (document: DocumentInterface) => void;
	document: DocumentInterface;
	hideDocumentType?: boolean;
	canApproveDocuments?: boolean;
	user?: string;
	cycleDocument?: (direction: "previous" | "next", document: DocumentInterface) => void;
	candidateName?: string;
};

export const canCiteDocument = (docType: RightToWork) => {
	if (!docType) {
		return false;
	}
	return rightToWorkCitationDocumentIds.includes(Number(docType));
};

const UpdateDocumentModal = ({ closeModal, confirmButtonAction, downloadDocument, deleteDocument, document, hideDocumentType, canApproveDocuments, user, cycleDocument, candidateName }: Props) => {
	const { RightToWorkAvailableItems, isLoading } = useCustomerDocuments();
	const [state, setState] = useState<DocumentInterface>(document);
	const [error, setError] = useState(null);
	const [unsaved, setUnsaved] = useState<boolean>(false);
	const [fullScreen, setFullScreen] = useState<boolean>(false);

	useEffect(() => {
		setState(document);
	}, [document]);

	const selectedDocument = useMemo(() => {
		return RightToWorkAvailableItems.find((item) => item.ID === Number(state.docType));
	}, [RightToWorkAvailableItems, state.docType]);

	const handleChange = ({ target }) => {
		setState((prevState) => ({
			...prevState,
			[target.name]: target.type === "checkbox" ? target.checked : target.value,
		}));
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		let valid = true;
		if (!state.docType) {
			valid = false;
		}
		if (valid) {
			setUnsaved(false);
			confirmButtonAction(state);
		} else {
			setError(<FormError errors={["Please enter the required type and dates."]} />);
		}
	};

	const toggleFullScreen = useCallback(() => {
		setFullScreen((prev) => !prev);
	}, []);

	const handleApproval = useCallback(() => {
		// If already approved, toggle back to null (unapprove)
		if (state.approved === true) {
			setState({ ...state, approved: null, approvedAt: null, approvedBy: null });
			setUnsaved(true);
			return;
		}

		// If not approved, validate citation compliance fields before approving
		if (state.approved === undefined || state.approved === null) {
			const validationResult = ValidateDocumentCitationCompliance(state);

			if (!validationResult.valid) {
				setError(<FormError errors={validationResult.errors.map((err) => err.error)} />);
				return;
			}

			// Clear any errors and proceed with approval
			setError(null);
			setState({ ...state, approved: true, approvedAt: new Date(), approvedBy: user });
			setUnsaved(true);
		}
	}, [state, user]);

	const handleDecline = useCallback(() => {
		if (state.approved === false) {
			setState({ ...state, approved: null, approvedAt: null, approvedBy: null });
		}
		if (state.approved === undefined || state.approved === null) {
			setState({ ...state, approved: false, approvedAt: new Date(), approvedBy: user });
		}
		setUnsaved(true);
	}, [state, user]);

	return (
		<>
			<ModalWrapper large>
				<ModalHeader>
					<H2>Edit Document{candidateName ? `: ${candidateName}` : ""}</H2>
					<ModalClose onClick={closeModal}>close</ModalClose>
				</ModalHeader>
				{!hideDocumentType && cycleDocument ? (
					<ModalToggleButtons>
						<ModalCycleButton direction="previous" onClick={() => cycleDocument("previous", document)}>
							Previous
						</ModalCycleButton>
						<ModalCycleButton direction="next" onClick={() => cycleDocument("next", document)}>
							Next
						</ModalCycleButton>
					</ModalToggleButtons>
				) : null}
				<ModalContent>
					<Row>
						{!hideDocumentType || canApproveDocuments ? (
							<Col span={5} span1024={12}>
								<Form>
									{!hideDocumentType ? (
										<>
											<Note>Choose Document Type and Set Relevant Dates (if applicable)</Note>
											<Row span={12}>
												<Col span={8} span768={12}>
													<SelectInput
														label="Document Type"
														name="docType"
														value={state.docType}
														onChange={handleChange}
														options={RightToWorkAvailableItems}
														placeholder="Select Document Type"
													/>
													{selectedDocument?.hasIssued && <DateInput label="Issued Date" name="issued" value={state.issued} onChange={handleChange} />}
													{selectedDocument?.hasLastChecked && <DateInput label="Last Checked Date" name="issued" value={state.issued} onChange={handleChange} />}
													{(selectedDocument?.hasExpiry ||
														selectedDocument?.ID == RightToWork.Qualification ||
														selectedDocument?.ID == RightToWork.Other ||
														selectedDocument?.ID == RightToWork.DBS) && <DateInput label="Expiry" name="expiry" value={state.expiry} onChange={handleChange} />}
												</Col>
											</Row>
											<Divider />
											<CheckboxInput
												label="Archive document?"
												note="Archived documents do not affect candidate compliance. Mark invalid, unused or historical documents as archived."
												name="documentArchived"
												value={state.documentArchived}
												onChange={handleChange}
											/>
										</>
									) : null}
									{canApproveDocuments ? (
										<div>
											<Divider />
											<Note pushBottom={true} help={true}>
												Toggle internal document approval status below.
												<InputHelp
													help={{
														modalTitle: "Document Approval",
														modalContent: <HelpModal title="Document Approval" content={DocumentApprovalStatusHelpText} />,
													}}
												/>
											</Note>
											{canCiteDocument(state.docType as unknown as RightToWork) ? (
												<>
													<div className="new-flag new-flag-offset">
														<CheckboxInput
															label="Cited Original Documentation"
															note="Confirm that original documentation was cited (required)"
															name="citedOriginalDocumentation"
															value={state.citedOriginalDocumentation}
															onChange={handleChange}
															help={{
																modalTitle: "Document Citation",
																modalContent: <HelpModal title="Document Citation" content={DocumentCitationHelpText} />,
															}}
														/>
													</div>

													{state.citedOriginalDocumentation && (
														<DateInput
															label="Citation Date"
															name="citedOriginalDocumentationDate"
															value={
																state.citedOriginalDocumentationDate instanceof Date
																	? moment(state.citedOriginalDocumentationDate).format("YYYY-MM-DD")
																	: state.citedOriginalDocumentationDate
															}
															onChange={handleChange}
															required
														/>
													)}
													{state.citedOriginalDocumentation && (
														<SelectInput
															label="Method of Citation"
															name="methodOfCitation"
															value={state.methodOfCitation || ""}
															onChange={handleChange}
															options={MethodOfCitationOptions}
															placeholder="Select Method of Citation"
														/>
													)}
													{(state.methodOfCitation === MethodOfCitation.LiveVideo || state.methodOfCitation === MethodOfCitation.DayOneReconciliation) && (
														<TextareaInput
															label="Reason for Citation Method"
															note={"Please provide a reason for using the selected method of citation."}
															name="reasonForCitationMethod"
															value={state.reasonForCitationMethod || ""}
															onChange={handleChange}
															required
														/>
													)}
												</>
											) : null}
											<ButtonList pushBottom={true}>
												{state.approved !== false ? (
													<StandardButtonAnchor small onClick={handleApproval}>
														{state.approved === true ? `Approved: ${state.approvedBy}, ${moment(state.approvedAt).format("DD/MM/YYYY HH:mm")}` : "Approve"}
													</StandardButtonAnchor>
												) : null}
												{state.approved !== true ? (
													<StandardCancelAnchor small onClick={handleDecline}>
														{state.approved === false ? `Declined: ${state.approvedBy}, ${moment(state.approvedAt).format("DD/MM/YYYY HH:mm")}` : "Decline"}
													</StandardCancelAnchor>
												) : null}
											</ButtonList>
											{unsaved ? <Note style={{ marginTop: 10 }}>Be sure to click &quot;Update Document&quot; to save your document changes.</Note> : null}
										</div>
									) : null}
									{!canApproveDocuments ? (
										<>
											<Divider />
											<Note pushBottom={true}>
												{state.approved === true
													? `Approved: ${state.approvedBy}, ${moment(state.approvedAt).format("DD/MM/YYYY HH:mm")}`
													: state.approved === false
														? `Declined: ${state.approvedBy}, ${moment(state.approvedAt).format("DD/MM/YYYY HH:mm")}`
														: ""}
											</Note>
											{state.approved === true && state.citedOriginalDocumentation && (
												<>
													<Note style={{ marginBottom: 10, fontWeight: "bold" }}>Citation Compliance Information</Note>
													<Note pushBottom={true}>
														<strong>Original Documentation Cited:</strong> Yes
													</Note>
													{state.citedOriginalDocumentationDate && (
														<Note pushBottom={true}>
															<strong>Citation Date:</strong> {moment(state.citedOriginalDocumentationDate).format("DD/MM/YYYY")}
														</Note>
													)}
													{state.methodOfCitation && (
														<Note pushBottom={true}>
															<strong>Method of Citation:</strong>{" "}
															{MethodOfCitationOptions.find((opt) => opt.ID === state.methodOfCitation)?.Name || state.methodOfCitation}
														</Note>
													)}
													{state.reasonForCitationMethod && (
														<Note pushBottom={true}>
															<strong>Reason for Citation Method:</strong> {state.reasonForCitationMethod}
														</Note>
													)}
												</>
											)}
										</>
									) : null}
									{error ? error : null}
									{!hideDocumentType ? (
										<>
											<Divider />
											<ButtonList justify="flex-end" pushBottom={true}>
												<StandardButtonAnchor onClick={handleSubmit}>Update Document</StandardButtonAnchor>
											</ButtonList>
										</>
									) : null}
								</Form>
							</Col>
						) : null}
						<Col span={!hideDocumentType || canApproveDocuments ? 7 : 12} span1024={12}>
							<div>{document ? <FilePreview document={document} closeModal={closeModal} toggleFullScreen={toggleFullScreen} /> : null}</div>
						</Col>
					</Row>
					{error ? error : null}
				</ModalContent>
				<ModalFooter>
					<ButtonList>
						<StandardCancelAnchor onClick={closeModal}>Cancel</StandardCancelAnchor>
						{deleteDocument ? <StandardCancelAnchor onClick={() => deleteDocument(document)}>Delete Document</StandardCancelAnchor> : null}
					</ButtonList>
					<ButtonList justify="flex-end">{!hideDocumentType ? <StandardButtonAnchor onClick={handleSubmit}>Update Document</StandardButtonAnchor> : null}</ButtonList>
				</ModalFooter>
			</ModalWrapper>
			{fullScreen ? (
				<Modal isOpen={fullScreen ? true : false} onRequestClose={() => setFullScreen(false)} style={ModalStylesLarge} contentLabel="" shouldCloseOnOverlayClick={true} ariaHideApp={false}>
					<FilePreviewModal closeModal={() => setFullScreen(false)} document={document} />
				</Modal>
			) : null}
		</>
	);
};

const mapDispatchToProps = {
	toggleLoading,
	setAlert,
};

export default connect(null, mapDispatchToProps)(UpdateDocumentModal);
